import type { ECharts } from "echarts/core";
import { useCallback, useMemo, useRef } from "react";
import { useDataViewerStore } from "../data-viewer-provider";

type ZrPointerEvent = { offsetX: number; offsetY: number };

// Track the panel x-axis movement so we can update a central "time" value that any external component
// can use to key into the stream cache, and display corresponding stream data.
export function useHoverTime() {
    const chartRef = useRef<ECharts | null>(null);
    const setTimestamp = useDataViewerStore((state) => state.setTimestamp);

    const onMouseMove = useCallback(
        (event: ZrPointerEvent) => {
            const chart = chartRef.current;
            if (!chart) return;

            const pointer: [number, number] = [event.offsetX, event.offsetY];
            if (!chart.containPixel({ gridIndex: 0 }, pointer)) {
                setTimestamp(null);
                return;
            }

            const xDataValue = chart.convertFromPixel({ xAxisIndex: 0 }, event.offsetX);
            if (xDataValue === undefined) return;
            setTimestamp(xDataValue);
        },
        [setTimestamp],
    );

    const onGlobalOut = useCallback(() => {
        setTimestamp(null);
    }, [setTimestamp]);

    const enable = useCallback(
        (chart: ECharts) => {
            if (chartRef.current && chartRef.current !== chart) {
                const prevZr = chartRef.current.getZr();
                prevZr.off("mousemove", onMouseMove);
                prevZr.off("globalout", onGlobalOut);
            }
            chartRef.current = chart;
            const zr = chart.getZr();
            zr.on("mousemove", onMouseMove);
            zr.on("globalout", onGlobalOut);
        },
        [onMouseMove, onGlobalOut],
    );

    const disable = useCallback(() => {
        if (!chartRef.current) return;
        const zr = chartRef.current.getZr();
        zr.off("mousemove", onMouseMove);
        zr.off("globalout", onGlobalOut);
        chartRef.current = null;
    }, [onMouseMove, onGlobalOut]);

    return useMemo(() => ({ enable, disable }), [enable, disable]);
}
